'use client';

import { formatDateTime } from '@pickleballinc/lib/format-dates';
import { getCDNURL } from '@pickleballinc/lib/get-cdn-url';
import { RankingStatusBadge } from '@pickleballinc/ui/components/global/RankingStatusBadge/RankingStatusBadge';
import { RankingStatusBadgeEnum } from '@pickleballinc/ui/components/global/RankingStatusBadge/types';
import { TOURNAMENT_PLACEHOLDER_IMAGE } from '@pickleballinc/ui/components/global/utils/constants';
import clsx from 'clsx';
import { useMemo, useState } from 'react';

import { MODULE_CARD_APP_COLORS } from '../../module-homepage/utils/constants';

import { ModuleCard } from './ModuleCard';
import { ModuleCardBody } from './ModuleCardBody';

export function renderPlayerTourneyCardLabels(tournament: TournamentCardProps): string {
	if (tournament.isCanceled) {
		return 'canceled';
	}

	const tourneyEndDate = new Date(tournament.dateTo);
	const now = new Date();

	if (tourneyEndDate < now) {
		return 'completed';
	}

	if (tournament.isRegistrationClosed) {
		return 'registration-closed';
	}

	if (tournament.price > 0) {
		return 'price';
	}

	if (tournament.isCostPerEvent) {
		return 'cost-per-event';
	}

	return 'free';
}

export type UseFavoriteState = (
	id: string,
	initialIsFavorite?: boolean
) => {
	isFavorite: boolean;
	isFavoriteDisabled: boolean;
	isUserLoggedIn: boolean;
	onToggle: (event: React.MouseEvent, id?: string, isFavourite?: boolean | null) => void;
};

const useDefaultFavoriteState: UseFavoriteState = () => ({
	isFavorite: false,
	isFavoriteDisabled: true,
	isUserLoggedIn: false,
	onToggle: () => {}
});

export interface TournamentCardProps {
	id: string;
	dateFrom: string;
	dateTo: string;
	isCanceled?: boolean;
	isCostPerEvent?: boolean;
	currency: string;
	location: string;
	isRegistrationClosed: boolean;
	registrationDateClosed?: Date | undefined;
	logo: string;
	price: number;
	registrationCount: number;
	slug: string;
	status?: string;
	title: string;
	isTournamentsApp?: boolean;
	tournamentsAppBaseURL: string;
	additionalImages?: string[];
	customCardHref?: string;
	/**
	 * Render the click-through as a plain `<a>` (full document navigation) instead of a Next `<Link>`.
	 * Set by the listing-ad rotator: an ad's URL carries Everflow parameters that the landing middleware
	 * has to see, and Next would route a same-origin URL client-side and skip it.
	 */
	nativeAnchor?: boolean;
	isAdvertiseOnly?: boolean;
	hideRegisteredPlayers?: boolean;
	className?: string;
	logoClassName?: string;
	badgeTitle?: RankingStatusBadgeEnum;
	showBadgeTitle?: boolean;
	enableRankingBadges?: boolean;
	hideCardHref?: boolean;
	showFavoriteIcon?: boolean;
	useFavoriteState?: UseFavoriteState;
	initialIsFavorite?: boolean;
	variant?: 'light' | 'dark';
	onClose?: () => void;
	isMapView?: boolean;
}

export const TournamentCard = (props: TournamentCardProps) => {
	const { id, slug, customCardHref, tournamentsAppBaseURL, status, hideCardHref = false } = props;

	const newStatus = status ? status : renderPlayerTourneyCardLabels(props);

	const href = hideCardHref
		? undefined
		: customCardHref
			? customCardHref
			: `${tournamentsAppBaseURL}/tournaments/${slug ? slug : id}${newStatus === 'completed' ? '/winners-circle' : ''}`;

	return <TournamentCardComponent {...props} status={newStatus} href={href} />;
};

const TournamentCardComponent = ({
	id,
	dateFrom,
	dateTo,
	logo,
	currency,
	location,
	price,
	registrationCount,
	registrationDateClosed,
	status,
	title,
	additionalImages,
	hideRegisteredPlayers,
	isAdvertiseOnly,
	className,
	logoClassName,
	href,
	nativeAnchor,
	showFavoriteIcon,
	badgeTitle,
	showBadgeTitle,
	enableRankingBadges = false,
	initialIsFavorite,
	variant = 'light',
	useFavoriteState,
	onClose,
	isMapView = false
}: TournamentCardProps & { href?: string }) => {
	const tourneyFromDate = new Date(dateFrom);
	const tourneyToDate = new Date(dateTo);
	const currentDate = new Date();
	const isDarkVariant = variant === 'dark';

	const formattedFromDate = formatDateTime(dateFrom, { parseZone: true });
	const formattedToDate = formatDateTime(dateTo, { parseZone: true });

	const isPlayingNow = currentDate >= tourneyFromDate && currentDate <= tourneyToDate && status !== 'completed';

	const [isPlaceholderImage, setIsPlaceholderImage] = useState(false);

	const isLessThan48Hours =
		registrationDateClosed && registrationDateClosed instanceof Date
			? registrationDateClosed.getTime() - currentDate.getTime() >= 0 &&
				registrationDateClosed.getTime() - currentDate.getTime() <= 48 * 60 * 60 * 1000
			: false;

	const getImageToDisplay = useMemo(() => {
		if (logo && (logo.toLowerCase().includes('/uploads/clubs/') || logo.toLowerCase().includes('/uploads/events/'))) {
			return getCDNURL(logo, 566, 384);
		}
		setIsPlaceholderImage(true);
		return getCDNURL(TOURNAMENT_PLACEHOLDER_IMAGE, 566, 384);
	}, [logo]);

	const images = additionalImages && additionalImages.length > 0 ? [getImageToDisplay, ...additionalImages] : [getImageToDisplay];

	const statusLabel = isPlayingNow
		? 'Playing Now'
		: status === 'completed'
			? 'Completed'
			: status === 'registration-closed'
				? 'Reg. Closed'
				: status === 'price'
					? `${currency ? currency.toUpperCase() : ''} ${new Intl.NumberFormat('en-US', { style: 'currency', currency: currency, currencyDisplay: 'narrowSymbol' }).format(price)}`
					: status === 'cost-per-event'
						? 'Cost Per Event'
						: status === 'free'
							? 'Free'
							: null;

	const playersLabel = !hideRegisteredPlayers
		? isAdvertiseOnly
			? 'Advertised'
			: `${registrationCount} player${registrationCount != 1 ? 's' : ''}`
		: null;

	const showFooterRow = Boolean(status || registrationCount);

	const metaLines = [{ id: 'date-range', content: dateFrom || dateTo ? `${formattedFromDate} - ${formattedToDate}` : null }];

	const { isFavorite, isFavoriteDisabled, isUserLoggedIn, onToggle } = (useFavoriteState ?? useDefaultFavoriteState)(id, initialIsFavorite);

	const colors = MODULE_CARD_APP_COLORS.tournament;

	return (
		<div className="flex flex-col h-full gap-2">
			{enableRankingBadges ? <RankingStatusBadge rankingStatus={badgeTitle} showBadge={showBadgeTitle} /> : null}
			<div className="relative w-full max-w-[343px] mx-auto min-[500px]:mx-0 min-w-[224px] min-[500px]:max-w-[286px]">
				<ModuleCard
					id={id}
					images={images}
					imageAlt={title}
					isPlaceholderImage={isPlaceholderImage}
					href={href}
					nativeAnchor={nativeAnchor}
					className={className}
					imageContainerClassName={logoClassName}
					imageClassName={clsx(logoClassName)}
					showFavoriteIcon={showFavoriteIcon}
					isUserLoggedIn={isUserLoggedIn}
					isFavourite={isFavorite}
					isFavoriteDisabled={isFavoriteDisabled}
					onFavoriteToggle={onToggle}
					isMapView={isMapView}
					onClose={onClose}
					topLeftSlot={
						isLessThan48Hours && status !== 'closed' ? (
							<div className="absolute top-2 left-2 z-30 px-2 py-1 text-sm rounded-md bg-success-700 text-white">Closing Soon</div>
						) : null
					}
					smallCard={isDarkVariant}
					backgroundColor={isDarkVariant ? colors.color : undefined}
					backgroundHoverColor={isDarkVariant ? colors.hoverColor : undefined}
					isPartnerCard={isDarkVariant}
				>
					<ModuleCardBody
						title={title}
						subtitle={location}
						metaLines={metaLines}
						footerLeft={showFooterRow ? statusLabel : null}
						footerLeftIsPrice={status === 'price'}
						footerRight={showFooterRow ? playersLabel : null}
						variant={variant}
					/>
				</ModuleCard>
			</div>
		</div>
	);
};
